/* eslint-disable no-param-reassign */
import { NavigateFunction, useNavigate, useSearchParams } from "react-router-dom";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  UIEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Switch,
} from "@mui/material";
import { LocationContext, SuccessAlertContext } from "App";
import { Search } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired } from "util/auth";
import { AxiosError } from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useOrderList from "../hooks/useOrderList";
import OrderContainer from "../components/OrderContainer";
import OrderTable from "../components/OrderTable";
import OrderSearchDialog from "../components/OrderSearchDialog";
import useOrderSearch from "../hooks/useOrderSearch";
import { Order, OrderConfirmErrorResponseApi, OrderHistory, OrderStatus, SearchApiParams } from "../types";
import {
  checkBusinessDate,
  convertUTCToDate,
  extractNoDupplicationSupplier,
  localeStringDisZero,
  orderStatusToNumber,
  removeEmptyValues,
  toOrderCheck,
} from "../utils/common";
import {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import { localEnv, hasRepresentativeCd, INVALID_DATE, orderStatusOptionList } from "../constants";
import downloadOrderAskCsv from "../api/downloadOrderAskCsv";
import CsvModals from "../components/CsvModals";
import OrderConfirmDialog from "../components/OrderConfirmDialog";
import updateOrderApi from "../api/updateOrderApi";
import { SelectForm } from "../components/Common";
import updateOrderHistoryStatusApi from "../api/updateOrderHistoryStatusApi";
import CustomTooltip from "../components/CustomTooltip";
import SelectAskDownloadMode from "../components/SelectAskDownloadMode";

const limit = 10;
const styles: { [key: string]: SxProps } = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  activeButton: {
    backgroundColor: "secondary.main",
    color: "var(--white)",
    "&:hover": {
      backgroundColor: "secondary.light",
    },
  },
  supplierTopCol: {
    // textAlign: 'center',
    p: 0.5,
    borderBottom: "none",
    height: 58,
  },
  supplierBottomCol: {
    // textAlign: 'center',
    p: 0.5,
    top: 67,
  },
};

/**
 * ヘッダーを返す
 * @param isModeAsk 依頼モードかどうか
 * @param onBack 戻る
 * @param isNoSelectedOrder 一つも注文が選ばれていなければtrue
 * @param setIsOrderSearch 検索モーダルを開閉
 * @param setIsPlaceOrder 発注モーダル開閉
 * @param setIsUploadOrderAskCsv 発注CSVモーダル開閉
 * @param setIsDownloadOrderAskCsv 依頼ダウンロード開閉
 * @param navigate 画面遷移等に使用
 * @param params パラメータ
 * @param isHistoryEditMode 履歴モードでステータスが変更可能な編集モードか閲覧モードか
 * @param setIsHistoryEditMode 履歴モードで編集、閲覧モード切り替え
 * @returns コンポーネント
 */
function HeaderBox(
  isModeAsk: boolean,
  onBack: Function,
  isNoSelectedOrder: boolean,
  setIsOrderSearch: Dispatch<SetStateAction<boolean>>,
  setIsPlaceOrder: Dispatch<SetStateAction<boolean>>,
  setIsUploadOrderAskCsv: Dispatch<SetStateAction<boolean>>,
  setIsDownloadOrderAskCsv: Dispatch<SetStateAction<boolean>>,
  navigate: NavigateFunction,
  params: Omit<SearchApiParams, "limit" | "offset">,
  isHistoryEditMode: boolean,
  setIsHistoryEditMode: Dispatch<SetStateAction<boolean>>,
) {
  return (
    <>
      <Box sx={styles.buttonContainer}>
        <Box sx={styles.leftButtonContainer}>
          <Box mx={1}>
            <Button onClick={() => onBack()} variant="custom-outlined">
              <Box>＜ 戻る</Box>
            </Button>
          </Box>
        </Box>
        {!isModeAsk && (
          <Box mx={1}>
            <Button onClick={() => setIsHistoryEditMode((prev) => !prev)} variant="contained">
              <Box>{isHistoryEditMode ? "閲覧モードへ" : "編集モードへ"}</Box>
            </Button>
          </Box>
        )}
        <Box mx={1}>
          <Button onClick={() => setIsOrderSearch(true)} variant="contained">
            <Search />
            <Box>絞込検索</Box>
          </Button>
        </Box>
        <Box mx={1}>
          <Button onClick={() => navigate("/order/form?mode=create")} variant="contained">
            <Box>注文作成</Box>
          </Button>
        </Box>
        {isModeAsk && (
          <Box mx={1}>
            <Button
              onClick={() => setIsPlaceOrder(true)}
              variant="contained"
              disabled={isNoSelectedOrder}
            >
              <Box>発注</Box>
            </Button>
          </Box>
        )}
      </Box>
      {isModeAsk && (
        <>
          <Box marginTop={1} />
          <Box sx={styles.buttonContainer}>
            <Box mx={1}>
              <Button
                onClick={() => setIsDownloadOrderAskCsv(true)}
                variant="contained"
              >
                <Box>注文依頼CSVダウンロード</Box>
              </Button>
            </Box>
            <Box mx={1}>
              <Button onClick={() => setIsUploadOrderAskCsv(true)} variant="contained">
                <Box>注文依頼CSVアップロード</Box>
              </Button>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

/**
 * 本部用注文商品一覧
 * @returns JSX.Element
 */
export default function OrderList() {
  const [isOrderSearch, setIsOrderSearch] = useState(false);
  const [isPlaceOrder, setIsPlaceOrder] = useState(false);
  const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);
  const [isUploadOrderAskCsv, setIsUploadOrderAskCsv] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useContext(SuccessAlertContext);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [searchParams] = useSearchParams();
  const isModeAsk = searchParams.get("mode") === "ask";
  const [isDownloadOrderAskCsv, setIsDownloadOrderAskCsv] = useState(false);

  // 注文履歴モードでのステータスアップデートの状態保持
  const [isHistoryEditMode, setIsHistoryEditMode] = useState(false);
  const [isUpdateStatusLoading, setIsUpdateStatusLoading] = useState(false);

  // 検索条件のstate
  const [
    {
      isRepresentativeCd,
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
    },
    {
      setIsRepresentativeCd,
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setjanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions },
    { isLoading: isOptionsLoading },
  ] = useOrderSearch(true);

  const [
    {
      total,
      orderList,
      orderHistoryList,
      isLoading: isOrderLoading,
      isMoreLoading,
      isFastLoading,
      fireMoreLoadEvent,
    },
    {
      setOrderList,
      setOrderHistoryList,
      get,
      getMore: getMoreOrderList,
      setFireMoreLoadEvent,
      setInit,
      setTotal,
    },
  ] = useOrderList(showAlert, isModeAsk);
  const navigate = useNavigate();

  const representativeParam = () => {
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 2 ) return 0
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 1 ) return 1
    return undefined;
  }

  const orderStatusParam = () => {
    if (isModeAsk) {
      return '1,101'; // 依頼では確認中しか選択肢がないため固定。
    }
    if (orderStatus === "") {
      return '2,3,4,5,9'; // 履歴は発注待ち以降
    }
    return `${orderStatusToNumber(orderStatus)}`;
  }

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): SearchApiParams => {
    const params: Omit<SearchApiParams, "limit" | "offset"> = {
      screen_type: 20,
      sort_type: isModeAsk ? 2 : 1, // 依頼 ? 注文書作成日昇順 : 注文日降順
      is_customer_order: (isCustomerOrder !== undefined ? Number(isCustomerOrder) : undefined),
      is_representative: representativeParam(),
      order_status: orderStatusParam(),
      store_name: shopName,
      author: storeOrderer,
      approver,
      // order_build_no: isModeAsk ? orderNumber : '',
      order_no: orderNumber,
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: janCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
      order_date_start: convertUTCToDate(orderStartDate),
      order_date_end: convertUTCToDate(orderEndDate),
    };
    return removeEmptyValues(params);
    // return params;
  };

  const cloneListRewrite = <T extends object, K extends keyof T>(
    prev: T[],
    key: K,
    value: T[K],
    index: number,
  ) => {
    const clonePrev = structuredClone(prev);
    clonePrev[index][key] = value;
    return clonePrev;
  };

  const cloneListMultiRewrite = <T extends object, K extends keyof T>(
    prev: T[],
    pairs: {
      key: K,
      value: T[K],
    }[],
    index: number,
  ) => {
    const clonePrev = structuredClone(prev);
    pairs.forEach(({key, value}) => {
      clonePrev[index][key] = value;
    });
    return clonePrev;
  };

  const handleSearch = async () => {
    setIsOrderSearch(false);
    setIsAllSelected(false); // 全選択を解除
    const params = createGetListApiParams();
    await get(params);
  };

  const handleEdit = (index: number) => {
    navigate(
      `/order/form?mode=edit&order-number=${orderList[index].orderNumber}&shop-cd=${orderList[index].shopCd}`,
    );
  };

  const handleCloseOrderConfirm = () => setIsPlaceOrder(false);
  const handleUpdateOrderConfirm = async () => {
    const oList = orderList.filter((o) => o.selected);
    setIsUpdateProcessing(true);
    let errorFlag = false;
    try {
      // ToDo: 更新の引数を決める必要あり
      const msg = await updateOrderApi({ orderList: toOrderCheck(oList) });
      Notify.setNotification({
        show: true,
        title: msg, // 確定処理をバッチで処理するためその旨を伝える
        confirmLabel: "OK",
        actionsPosition: "end",
      });
      // showAlert("発注しました。");
      setIsPlaceOrder(false);
      Notify.onClose = () => {
        // 取得し直し
        if (!errorFlag) get(createGetListApiParams());
      };
    } catch (err) {
      console.error(err);
      errorFlag = true;

      const error = err as AxiosError;
      setTimeout(() => {
        // レスポンス自体がなくエラーになった時
        if (!error.response) {
          Notify.setNotification({
            show: true,
            title: "APIに接続できませんでした。",
            confirmLabel: "OK",
            actionsPosition: "end",
          });
          return;
        }
        const { status } = error.response;
        const data = error.response.data as OrderConfirmErrorResponseApi;
        switch (status) {
          case 400:
            Notify.setNotification({
              show: true,
              title: "パラメータエラーです。",
              confirmLabel: "OK",
              actionsPosition: "end",
            });
            break;
          case 422:
            Notify.setNotification({
              show: true,
              title: `入力エラーがあります。`,
              message: data.messages.join("\n"),
              titleColor: "var(--error)",
            });
            break;
          default:
            break;
        }
      }, 0);
      showAlert("更新に失敗しました。", "error");
    } finally {
      setIsUpdateProcessing(false);
    }
  };

  const handleBack = () => navigate(-1);
  const tableHeadElement = () =>
    isModeAsk ? (
      <>
        <TableRow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            <Checkbox
              color="secondary"
              checked={isAllSelected}
              onChange={(_, check) => {
                const flag = orderList.every((o) => o.selected);
                setIsAllSelected(!flag);
                setOrderList((prev) => {
                  const newOrders = structuredClone(prev);
                  newOrders.forEach((o: Order) => {
                    o.selected = !flag;
                  });
                  return newOrders;
                });
              }}
              inputProps={{ "aria-label": "customer-selected" }}
            />
            <Box>{`\n\n全選択`}</Box>
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            編集
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>ステータス</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>注文番号</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>JANコード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>店舗名</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>仕入先名</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>ブランド</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={2}>
            商品名
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
          <TableHeaderCellWide sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
          <TableHeaderCellWide right sx={styles.supplierTopCol}>
            発注数
          </TableHeaderCellWide>
          <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
            確定時<br />エラー判定
          </TableHeaderCellNarrow>
        </TableRow>
        <TableRow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            本部原単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕切単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗売単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>納品希望日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷予定日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
            仕入先
            <br />
            入荷予定
            <CustomTooltip
              title={<>仕入先での入荷予定です。店舗の入荷予定ではありません。</>}
              arrow
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography
                  component="span"
                  sx={{ color: "var(--red-600)", position: "absolute", top: 0, right: 12 }}
                >
                  <HelpIcon sx={{ fontSize: 18 }} />
                </Typography>
              </Box>
            </CustomTooltip>
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>得意先伝票No.</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>客注</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>直送</TableHeaderCellNarrow>
          <TableHeaderCellWide sx={styles.supplierBottomCol}>店舗備考</TableHeaderCellWide>
          <TableHeaderCellWide sx={styles.supplierBottomCol}>本部備考</TableHeaderCellWide>
        </TableRow>
      </>
    ) : (
      <>
        <TableRow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>ステータス</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>注文番号</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>JANコード</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>店舗名</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>仕入先名</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>ブランド</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={2}>
            商品名
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
          <TableHeaderCellWide sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
          <TableHeaderCellWide right sx={styles.supplierTopCol}>
            発注数
          </TableHeaderCellWide>
          <TableHeaderCellWide right sx={styles.supplierTopCol}>
            納品数
          </TableHeaderCellWide>
        </TableRow>
        <TableRow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            本部原単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            仕切単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
            店舗売単価
            {/* <br />
            (単価) */}
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>納品希望日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷予定日</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
            仕入先
            <br />
            入荷予定
            <CustomTooltip
              title={<>仕入先での入荷予定です。店舗の入荷予定ではありません。</>}
              arrow
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography
                  component="span"
                  sx={{ color: "var(--red-600)", position: "absolute", top: 0, right: 12 }}
                >
                  <HelpIcon sx={{ fontSize: 18 }} />
                </Typography>
              </Box>
            </CustomTooltip>
          </TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>得意先伝票No.</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>客注</TableHeaderCellNarrow>
          <TableHeaderCellNarrow sx={styles.supplierBottomCol}>直送</TableHeaderCellNarrow>
          <TableHeaderCellWide sx={styles.supplierBottomCol}>店舗備考</TableHeaderCellWide>
          <TableHeaderCellWide sx={styles.supplierBottomCol} colSpan={2}>
            本部備考
          </TableHeaderCellWide>
        </TableRow>
      </>
    );

  const orderChangeStatus = async (order: OrderHistory, index: number, expectedShippingDate?: string, st?: OrderStatus) => {
    setIsUpdateStatusLoading(true);
    try {
      await updateOrderHistoryStatusApi({
        orderNumber: order.orderNumber,
        orderNumberStatus: st ? orderStatusToNumber(st) : undefined,
        orderDate: order.orderDate,
        expectedShippingDate: expectedShippingDate === '' ? '' : expectedShippingDate ? convertUTCToDate(expectedShippingDate) : undefined
      });
      // const isStatusChanged = !expectedShippingDate && expectedShippingDate !== '';
      showAlert("データを更新しました。", "success");
      if (st != null) {
        setOrderHistoryList((prev) =>
          cloneListMultiRewrite(prev, [
            {key: "orderStatus", value: st},
            {key: "isEditedExpectedShippingDate", value: true},
          ], index),
        );
      } else if (expectedShippingDate != null) {
        setOrderHistoryList((prev) =>
          cloneListMultiRewrite(prev, [
            {key: "expectedShippingDate", value: expectedShippingDate},
            {key: "isEditedExpectedShippingDate", value: true},
          ], index),
        );
      }
    } catch (err) {
      console.error(err);
      showAlert("データの更新に失敗しました。", "error");
    } finally {
      setIsUpdateStatusLoading(false);
    }
  }

  /**
   * 履歴の場合にステータスを変更できる編集モードと閲覧モードがある
   * @returns JSX.Element
   */
  function tableBodyHistory() {
    return isHistoryEditMode ? (
      <>
        {orderHistoryList.map((order, index) => {
          const isZebraStripeRow = index % 2 === 1;
          return (
            <Fragment key={`${order.id}:${order.orderDate}`}>
              <TableRow>
                <TableCellWide gray={isZebraStripeRow}>
                  <SelectForm
                    key="order_status"
                    id="order_status"
                    label="ステータス"
                    optionList={orderStatusOptionList.slice(4, 8)} // 空文字、発注前ステータスは含まない
                    value={order.orderStatus}
                    dummyValues={["未発注", "確認中", "発注待ち"]}
                    setValue={(val: OrderStatus) => orderChangeStatus(order, index, undefined, val)}
                    fullWidth
                  />
                </TableCellWide>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderNumber}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.productCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.janCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shopName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.supplierName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.classification}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.brand}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                  {order.productName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.size}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.productNumber}
                </TableCellWide>
                <TableCellWide right multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.orderCount)}
                </TableCellWide>
                <TableCellWide right multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.deliveryCount)}
                </TableCellWide>
              </TableRow>
              <TableRow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.headquartersCost)}
                  <br /> */}
                  {`${localeStringDisZero(order.headquartersUnitCost)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.wholesalePrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.shopPrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.shopUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.deliveryDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shippingDate}
                </TableCellNarrow>
                <TableCellWide gray={isZebraStripeRow}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                    <DatePicker
                      label="出荷予定日"
                      format="YYYY/MM/DD"
                      minDate={order.isEditedExpectedShippingDate ? dayjs().add(1, "day") : undefined}
                      value={
                        order.expectedShippingDate ? dayjs(order.expectedShippingDate, "YYYY/MM/DD") : null
                      }
                      onChange={(val) =>
                        setOrderHistoryList((prev) => cloneListRewrite(prev, "expectedShippingDate", val ? val.format("YYYY/MM/DD") : "", index))
                      }
                      slotProps={{ textField: { size: "small",
                        onClick: () => {
                          // エラー処理を有効にする
                          setOrderHistoryList((prev) =>
                            cloneListRewrite(prev,"isEditedExpectedShippingDate",true,index),
                          );
                        },
                        onBlur: (e) => {
                          const val = e.currentTarget.value;
                          console.log(val);
                          if (val && convertUTCToDate(val) !== INVALID_DATE && checkBusinessDate(dayjs(val)) && dayjs(val).isAfter(dayjs().add(1, "day"))) {
                            orderChangeStatus(order, index, val ?? "");
                          } else if (val && convertUTCToDate(val) === INVALID_DATE && val === 'YYYY/MM/DD') {
                            orderChangeStatus(order, index, ""); // 未入力の場合は空にする
                          } else {
                            showAlert('明日以降の営業日を入力してください。', 'error');
                          }
                        }
                      }}}
                      onOpen={() => setOrderHistoryList((prev) =>
                        // エラー処理を有効にする
                        cloneListRewrite(prev,"isEditedExpectedShippingDate",true,index),
                      )}
                      onAccept={(val: dayjs.Dayjs | null) => {
                        if (val && checkBusinessDate(val)) {
                          orderChangeStatus(order, index, val ? val.format("YYYY/MM/DD") : "")
                        }
                      }}
                      sx={{
                        width: "160px",
                      }}
                      shouldDisableDate={(date) => {
                        if (order.isEditedExpectedShippingDate && !checkBusinessDate(date)) {
                          return true; // 編集した後営業日でない場合は選択不可
                        }
                        return false;
                      }}
                      onError={(reason) => {
                        // setExpectedShippingDateError(
                        //   reason ? "出荷予定日の日付が無効です。" : null,
                        // );
                      }}
                    />
                  </LocalizationProvider>
                </TableCellWide>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.arrivalDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.clientBillNumber}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isCustomerOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isDirectOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.shopRemarks}
                </TableCellWide>
                <TableCellWide multi gray={isZebraStripeRow} colSpan={2}>
                  {order.headquartersRemarks}
                </TableCellWide>
              </TableRow>
            </Fragment>
          );
        })}
      </>
    ) : (
      <>
        {orderHistoryList.map((order, index) => {
          const isZebraStripeRow = index % 2 === 1;
          return (
            <Fragment key={`${order.id}:${order.orderDate}`}>
              <TableRow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderStatus}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderNumber}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.productCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.janCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shopName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.supplierName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.classification}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.brand}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                  {order.productName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.size}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.productNumber}
                </TableCellWide>
                <TableCellWide right multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.orderCount)}
                </TableCellWide>
                <TableCellWide right multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.deliveryCount)}
                </TableCellWide>
              </TableRow>
              <TableRow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.headquartersCost)}
                  <br /> */}
                  {`${localeStringDisZero(order.headquartersUnitCost)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.wholesalePrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.shopPrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.shopUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.deliveryDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shippingDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.expectedShippingDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.arrivalDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.clientBillNumber}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isCustomerOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isDirectOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.shopRemarks}
                </TableCellWide>
                <TableCellWide multi gray={isZebraStripeRow} colSpan={2}>
                  {order.headquartersRemarks}
                </TableCellWide>
              </TableRow>
            </Fragment>
          );
        })}
      </>
    );
  }

  const tableBodyElement = () =>
    isModeAsk ? (
      <>
        {orderList.map((order, index) => {
          const isZebraStripeRow = index % 2 === 1;
          return (
            <Fragment key={`${order.id}:${order.orderDate}`}>
              <TableRow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={2}>
                  <Checkbox // 選択のチェックボックス
                    color="secondary"
                    checked={order.selected}
                    onChange={(_, check) => {
                      setOrderList((prev) => {
                        const newOrder = structuredClone(prev);
                        newOrder[index].selected = check;
                        return newOrder;
                      });
                    }}
                    inputProps={{ "aria-label": "customer-selected" }}
                  />
                </TableCellNarrow>
                <TableCellNarrow center gray={isZebraStripeRow} rowSpan={2}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => handleEdit(index)}>
                    <EditIcon />
                  </Box>
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderStatus === orderStatusOptionList[9] ? orderStatusOptionList[2] : order.orderStatus}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderPlainNumber || ''}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.productCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.janCd}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shopName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.supplierName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.classification}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.brand}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                  {order.productName}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.size}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.productNumber}
                </TableCellWide>
                <TableCellWide right multi gray={isZebraStripeRow}>
                  {localeStringDisZero(order.orderCount)}
                </TableCellWide>
                <TableCellNarrow center multi gray={isZebraStripeRow} rowSpan={2}>
                  {order.confirmErrMsg}
                </TableCellNarrow>
              </TableRow>
              <TableRow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.headquartersCost)}
                  <br /> */}
                  {`${localeStringDisZero(order.headquartersUnitCost)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.wholesalePrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow right multi gray={isZebraStripeRow}>
                  {/* {localeStringDisZero(order.shopPrice)}
                  <br /> */}
                  {`${localeStringDisZero(order.shopUnitPrice)}`}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.orderDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.deliveryDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.shippingDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.expectedShippingDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.arrivalDate}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.clientBillNumber}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isCustomerOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellNarrow multi gray={isZebraStripeRow}>
                  {order.isDirectOrder ? "〇" : "×"}
                </TableCellNarrow>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.shopRemarks}
                </TableCellWide>
                <TableCellWide multi gray={isZebraStripeRow}>
                  {order.headquartersRemarks}
                </TableCellWide>
              </TableRow>
            </Fragment>
          );
        })}
      </>
    ) : (
      tableBodyHistory()
    );

  // スクロール処理
  const handleScroll = async (e: UIEvent<HTMLDivElement>) => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current || isMoreLoading) {
      return;
    }
    // const scrollTop = window.scrollY;
    // const windowHeight = window.innerHeight;
    // const documentHeight = document.documentElement.scrollHeight;
    const { scrollTop } = e.currentTarget;
    const windowHeight = e.currentTarget.clientHeight;
    const documentHeight = e.currentTarget.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll event:");
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!localEnv && !(userType === "C0001" || userType === "C0005")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "本部ユーザーでログインしてください。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (!localEnv && checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else {
          // 初期表示
          await get(createGetListApiParams());
          setInit(true);
        }
      })();
    }, 0);
  }, []);

  // const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  // useEffect(() => {
  //   const oLength = isModeAsk ? orderList.length : orderHistoryList.length;
  //   if (limit <= oLength && oLength < total && !isLoading && !isFastLoading.current) {
  //     if (!checkScrollable()) {
  //       (async() => {
  //         await getMoreOrderList(createGetListApiParams());
  //       })();
  //     }
  //   }
  // }, [orderList, orderHistoryList, isLoading]);

  // useEffect(() => {
  //   (async () => {
  //     setInit(true);
  //   })();
  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   }
  // }, []);

  // ローディングの統一
  useEffect(() => {
    setIsLoading(isOrderLoading || isOptionsLoading);
  }, [isOrderLoading, isOptionsLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreOrderList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  const checkOrderSelected = () => orderList.every((o) => !o.selected);

  return (
    <>
      <OrderContainer
        orderTitle={isModeAsk ? "注文依頼一覧" : "注文履歴一覧"}
        OrderTable={
          <OrderTable
            searchLen={total}
            isAllLoaded={orderList.length >= total || orderHistoryList.length >= total}
            tableHeadElement={() => tableHeadElement()}
            tableBodyElement={() => tableBodyElement()}
            isLoading={isLoading}
            isMoreLoading={isMoreLoading}
            handleScroll={handleScroll}
            limit={limit}
            total={total}
            orderListLength={0}
            isFastLoading={isFastLoading}
            getMoreOrderList={getMoreOrderList}
            createGetListApiParams={createGetListApiParams}
          />
        }
        HeaderBox={HeaderBox(
          isModeAsk,
          handleBack,
          checkOrderSelected(),
          setIsOrderSearch,
          setIsPlaceOrder,
          setIsUploadOrderAskCsv,
          setIsDownloadOrderAskCsv,
          navigate,
          createGetListApiParams(),
          isHistoryEditMode,
          setIsHistoryEditMode,
        )}
        isLoading={isLoading}
        helpPath={isModeAsk ? "/resources/orders/manual_order_product_ask.pdf" : "/resources/orders/manual_order_product.pdf"}
      />
      {!isLoading && (
        <OrderSearchDialog
          open={isOrderSearch}
          onClose={() => setIsOrderSearch(false)}
          onSearch={handleSearch}
          isRepresentativeCd={isRepresentativeCd}
          isCustomerOrder={isCustomerOrder}
          shopName={shopName}
          orderStatus={orderStatus}
          orderReceiptStatus={orderReceiptStatus}
          storeOrderer={storeOrderer}
          approver={approver}
          orderStartDate={orderStartDate}
          orderEndDate={orderEndDate}
          companyCd={companyCdField}
          companyName={companyName}
          centerCd={centerCd}
          orderNumber={orderNumber}
          productCd={productCd}
          productName={productName}
          brand={brand}
          janCd={janCd}
          productNumber={productNumber}
          tyreWidth={tyreWidth}
          tyreRatio={tyreRatio}
          tyreRimDiam={tyreRimDiam}
          wheelWidth={wheelWidth}
          wheelPcd={wheelPcd}
          wheelHole={wheelHole}
          wheelRimDiam={wheelRimDiam}
          classification={classification}
          supplier={supplier}
          tyreOptions={tyreOptions}
          wheelOptions={wheelOptions}
          classificationOptions={classificationOptions}
          brandOptions={brandOptions}
          setIsRepresentativeCd={setIsRepresentativeCd}
          setIsCustomerOrder={setIsCustomerOrder}
          setShopName={setShopName}
          setOrderStatus={setOrderStatus}
          setOrderReceiptStatus={setOrderReceiptStatus}
          setStoreOrderer={setStoreOrderer}
          setApprover={setApprover}
          setOrderStartDate={setOrderStartDate}
          setOrderEndDate={setOrderEndDate}
          setCompanyCd={setCompanyCdField}
          setCompanyName={setCompanyName}
          setCenterCd={setCenterCd}
          setProductCd={setProductCd}
          setOrderNumber={setOrderNumber}
          setProductName={setProductName}
          setBrand={setBrand}
          setjanCd={setjanCd}
          setProductNumber={setProductNumber}
          setTyreWidth={setTyreWidth}
          setTyreRatio={setTyreRatio}
          setTyreRimDiam={setTyreRimDiam}
          setWheelWidth={setWheelWidth}
          setWheelPcd={setWheelPcd}
          setWheelHole={setWheelHole}
          setWheelRimDiam={setWheelRimDiam}
          setClassification={setClassification}
          setSupplier={setSupplier}
          setCategory={setCategory}
          screenType={isModeAsk ? "本部依頼一覧" : "本部履歴一覧"}
        />
      )}
      <CsvModals
        refresh={() => get(createGetListApiParams())}
        isOpenOrderAskCsvUpload={isUploadOrderAskCsv}
        setIsOpenOrderAskCsvUpload={setIsUploadOrderAskCsv}
      />
      <OrderConfirmDialog
        open={isPlaceOrder}
        onClose={() => handleCloseOrderConfirm()}
        onUpdate={() => handleUpdateOrderConfirm()}
        selectedOrderList={orderList.filter((o) => o.selected)}
        isUpdateProcessing={isUpdateProcessing}
      />
      <SelectAskDownloadMode
        open={isDownloadOrderAskCsv}
        onClose={() => setIsDownloadOrderAskCsv(false)}
        downloadCsv={async (dlMode) => {await downloadOrderAskCsv({ dl_mode: dlMode, ...createGetListApiParams(), limit: 10000000 })}}
      />
      {isUpdateStatusLoading && ( // 履歴モードかつ、ステータス編集モードの時のステータスアップデート時の画面全体のマスク
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
